import { Subject } from "rxjs";
import { logD } from "@blacknut/logging/dist";
const LOGGING_TAG = "Elecron";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const electron = (window as any).electron;

const isElectronAvailable = () => {
  return !!electron;
};

export enum ElectronEventType {
  onUpdateDownloaded = "onUpdateDownloaded",
  onUpdateAvailable = "onUpdateAvailable",
  onUpdateError = "onUpdateError",
  devToolsChange = "devToolsChange",
  uncaughtException = "uncaughtException",
  deeplinkingUrl = "deeplinkingUrl",
  onNavigateInPage = "onNavigateInPage",
  onDeviceInfo = "onDeviceInfo",
  postToken = "postToken",
}

export declare interface ElectronEvent {
  event: string;
  data?: unknown;
}

export class ElectronService {
  private platform = "web";
  private subject: Subject<ElectronEvent> = new Subject();
  private _version = "";

  private _debugFromCommandLine = false;
  public get debugFromCommandLine(): boolean {
    return this._debugFromCommandLine;
  }
  public set debugFromCommandLine(v: boolean) {
    this._debugFromCommandLine = v;
    this.sendDebug(v);
  }

  constructor() {
    if (!isElectronAvailable()) {
      return;
    }
    this.platform = electron.getPlatform();

    // Handle navigation intra page
    electron.onNavigateInPage(
      (e: unknown, data: { url: string; canGoBack: boolean }) => {
        this.subject.next({ event: ElectronEventType.onNavigateInPage, data });
      },
    );

    // Handle Exception
    electron.onUncaughtException((error: unknown) => {
      this.subject.next({
        event: ElectronEventType.uncaughtException,
        data: error,
      });
    });

    // Handle update
    electron.onUpdateDownloaded((version: string) => {
      this.subject.next({
        event: ElectronEventType.onUpdateDownloaded,
        data: version,
      });
    });

    electron.onUpdateAvailable(() => {
      this.subject.next({ event: ElectronEventType.onUpdateAvailable });
    });

    electron.onUpdateError(() => {
      this.subject.next({ event: ElectronEventType.onUpdateError });
    });

    electron.onDeviceInfo((data: unknown) => {
      this.subject.next({ event: ElectronEventType.onDeviceInfo, data });
    });

    // get Deeplinking
    electron.onDeeplink((results: string | string[] | null) => {
      logD(LOGGING_TAG, "deeplinkingUrl %o %o", results);
      // Delete last char '/' and '\s'

      let url;
      if (typeof results === "string") {
        url = results;
      } else if (results && results.length) {
        url = results[0];
      }

      if (url) {
        //FIXME scheme
        const deeplinkingUrl = url
          ? url.replace(/(\/|\s)*$/, "").slice("blacknut:/".length)
          : null;
        this.subject.next({
          event: ElectronEventType.deeplinkingUrl,
          data: deeplinkingUrl,
        });
      }
    });

    electron.onPostToken((token: string) => {
      logD(LOGGING_TAG, "postToken %o %o", token);
      this.subject.next({
        event: ElectronEventType.postToken,
        data: token,
      });
    });

    const { appVersion }: { appVersion: string } = electron.getVersions();
    logD(LOGGING_TAG, "getVersions %o %o", appVersion);
    this._version = appVersion;
  }

  public recoverFromV3(): {
    token?: string;
    profilesData?: string;
    theme?: string;
  } {
    return electron && electron.recoverFromV3();
  }

  public isAvailable() {
    return isElectronAvailable();
  }

  public getCommandLineArgs() {
    return electron && electron.getCommandLineArgs();
  }

  public get version(): string {
    return this._version;
  }

  public onElectronSubject() {
    return this.subject.asObservable();
  }

  public getPlatform() {
    return this.platform;
  }

  public onMinimize() {
    return electron && electron.minimize();
  }

  public clearHistory() {
    return electron && electron.clearHistory();
  }

  public onFullscreen() {
    return electron && electron.maximize();
  }

  public requestFullscreen() {
    return electron && electron.requestFullscreen();
  }

  public requestFullscreenAvailable() {
    return !!electron && electron.requestFullscreen;
  }

  public getDeviceInfo() {
    return electron && electron.getDeviceInfo();
  }

  // Send close to electron
  public onClose() {
    return electron && electron.close();
  }

  public sendDebug(debug: boolean) {
    logD(LOGGING_TAG, "Sending debug to electron: %o ", debug);
    return electron && electron.toggleDebug(debug);
  }

  public killPlayer() {
    return electron && electron.killPlayer();
  }
  public checkUpdate() {
    return electron && electron.checkUpdate();
  }

  public canGoBack() {
    return electron && electron.ipcRenderer.canGoBack();
  }

  public acceptUpdate() {
    return electron && electron.acceptUpdate();
  }

  public closeWebView(id: string) {
    return electron && electron.closeWebView(id);
  }

  public getNetworkConnectionType(): Promise<string> {
    return electron && electron.getNetworkConnectionType();
  }
}

export default new ElectronService();
